/**
 * useCountdownTimer Hook
 *
 * Reusable countdown timer that decrements every second and calls onComplete when reaching 0.
 * Provides clean separation of timer logic for testing and reuse.
 */

import { useEffect } from 'react';

interface UseCountdownTimerOptions {
  /**
   * Current countdown value (null means no countdown active)
   */
  countdown: number | null;
  /**
   * Callback when countdown reaches 0
   */
  onComplete: () => void;
  /**
   * Callback to update countdown value
   */
  setCountdown: (value: number | null) => void;
  /**
   * Callback to set starting flag
   */
  setIsStarting: (value: boolean) => void;
}

/**
 * Custom hook to manage countdown timer logic
 *
 * @example
 * ```tsx
 * useCountdownTimer({
 *   countdown,
 *   onComplete: () => navigate('/games'),
 *   setCountdown,
 *   setIsStarting
 * });
 * ```
 */
export function useCountdownTimer({
  countdown,
  onComplete,
  setCountdown,
  setIsStarting
}: UseCountdownTimerOptions): void {
  useEffect(() => {
    if (countdown === null || countdown <= 0) {
      return;
    }

    const timerId = setTimeout(() => {
      const newCountdown = countdown - 1;
      setCountdown(newCountdown);

      // Auto-complete when countdown reaches 0
      if (newCountdown === 0) {
        setIsStarting(false);
        onComplete();
      }
    }, 1000);

    return () => {
      clearTimeout(timerId);
    };
  }, [countdown, onComplete, setCountdown, setIsStarting]);
}
